import { motion } from "framer-motion";
import { Church, BookOpen, Globe2 } from "lucide-react";
import ChapterMarker from "@/components/ChapterMarker";
import { AUTHOR_PHOTO } from "@/data/books";

const EASE = [0.16, 1, 0.3, 1];
const FACTS = [
  { icon: Church, label: "Priest & Theologian" },
  { icon: BookOpen, label: "Author of Five Books" },
  { icon: Globe2, label: "Canadian Storyteller" },
];

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative bg-abbey-light px-6 py-24 md:px-12 md:py-32 lg:px-24 lg:py-40"
    >
      <div className="mx-auto max-w-7xl">
        <ChapterMarker number="03" eyebrow="Chapter Three — The Author" title="About Fr. J. Stopa" />

        <div className="grid items-start gap-16 lg:grid-cols-2 lg:gap-24">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1, ease: EASE }}
            className="relative"
          >
            <div className="absolute -left-4 -top-4 h-full w-full border border-abbey-gold/50" />
            <img
              src={AUTHOR_PHOTO}
              alt="Fr. J. Stopa in his church"
              loading="lazy"
              style={{ objectPosition: "50% 18%" }}
              className="relative aspect-[4/5] w-full object-cover shadow-2xl"
              data-testid="about-author-photo"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1, ease: EASE, delay: 0.15 }}
          >
            <p className="drop-cap text-sm leading-loose text-abbey-ink/85 md:text-base">
              Fr. J. Stopa is a theologian, priest, and storyteller whose
              writing brings the ancient wisdom of Scripture into the ordinary
              moments of modern life. Rooted in a lifetime of ministry, his
              books move between candlelit sanctuaries and small-town soup
              kitchens, between whispered prayers and the quiet miracle of
              everyday grace.
            </p>
            <p className="mt-6 text-sm leading-loose text-abbey-ink/75 md:text-base">
              From the parables of Jesus retold for today's world, to Christmas
              tales that shimmer with hope, to Easter stories of resurrection
              renewed — each collection invites readers, book clubs, clergy,
              and the spiritually curious to find meaning, healing, and hope in
              a fractured world. These are not merely ancient stories, retold.
              They are stories about us.
            </p>

            <div className="mt-12 grid gap-4 sm:grid-cols-3" data-testid="about-facts">
              {FACTS.map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="border border-abbey-ink/15 p-5 transition-colors duration-300 hover:border-abbey-gold"
                >
                  <Icon className="h-5 w-5 text-abbey-rust" strokeWidth={1.5} />
                  <p className="mt-3 font-cinzel text-[10px] leading-relaxed tracking-[0.2em] text-abbey-ink/80">
                    {label.toUpperCase()}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
